import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Container,
  Divider,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ClientOnboardingApi from '../../../apis/ClientOnboardingApi';
import LoadingCircle from '../../../components/LoadingCircle/LoadingCircle';
import PricingTable from '../../../components/PricingTable/PricingTable';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { LicenseTierDto } from '../../../generated-types/licenseTierDto';
import { calculateTotal } from '../../../lib/calculateTotal';
import useApi from '../../../lib/useApi';

/**
 * The pricing calculator, which is used as the hero for the purchase training page
 */
const PricingCalculator = () => {
  const [licenseCount, setLicenseCount] = useState<number | ''>('');
  const [minimum, setMinimum] = useState(-1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setShowError] = useState(false);
  const [tiers, setTiers] = useState<LicenseTierDto[]>([]);
  const [showTiers, setShowTiers] = useState<any>(false);
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const snackbarContext = useContext(SnackbarContext);
  const theme = useTheme();

  /** Use effect to get the license tiers and minimum license count */
  useEffect(() => {
    const load = async () => {
      // Make the backend requests
      const loadLicenseTiersPromise = clientOnboardingApi.GetLicenseTiers();
      const min = await clientOnboardingApi.GetLicenseMinimum();

      // Set the states
      setMinimum(min);
      setLicenseCount(min);
      setTiers(await loadLicenseTiersPromise);
      setShowError(false);
    };

    setIsLoading(true);
    load()
      .catch(() => {
        snackbarContext.setAlert(
          <Alert severity="error" variant="filled">
            An error occurred while loading the pricing information
          </Alert>
        );
        snackbarContext.triggerSnackbar();
        setShowError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clientOnboardingApi, setTiers, snackbarContext]);

  /** Set the initial license total */
  useEffect(() => {
    const newTotal = licenseCount ? calculateTotal(licenseCount, tiers) : 0;
    setTotal(newTotal);
  }, [licenseCount, tiers]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      {error ? (
        <Container maxWidth="lg">
          <Box
            textAlign="center"
            mb={4}
            minHeight="20em"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              fontSize="4em"
              fontWeight={600}
            >
              Instant Team Pricing
            </Typography>
            <Typography variant="subtitle1" sx={{ marginBottom: '2em' }}>
              Our detailed pricing is currently unavailable. Please contact us
              directly for a personalized quote.
            </Typography>
            <Button component={Link} variant="contained" to="/contact">
              Contact us
            </Button>
          </Box>
          <Divider />
        </Container>
      ) : (
        <>
          <Box
            sx={{
              backgroundColor: (t) => t.palette.primary.main,
              pb: 15,
              width: '100%',
              color: 'white',
              textAlign: 'center',
              pt: 5,
            }}
          >
            <Container maxWidth="md" sx={{ my: 5 }}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                fontSize="4em"
                fontWeight={600}
              >
                Instant Team Pricing
              </Typography>
              <Typography variant="subtitle1" fontWeight={500} fontSize="1.1em">
                Calculate your custom quote for training licenses. Purchase as
                few as {minimum} or contact us for fewer.
              </Typography>
            </Container>
          </Box>
          <Card
            elevation={10}
            sx={{
              mt: -12,
              mx: 2,
              p: 3,
            }}
          >
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              alignItems="center"
              justifyContent="center"
              gap={4}
              mt={3}
            >
              <Box>
                <TextField
                  label="Number of Licenses"
                  type="number"
                  value={licenseCount}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10) || '';
                    setLicenseCount(val);
                  }}
                  helperText={`Minimum purchase is ${minimum} licenses. Contact us for fewer.`}
                />
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', md: 'block' } }}
              />
              <Box textAlign="center">
                <Typography variant="h6">Total Estimated Price</Typography>
                <Typography variant="h4">
                  {`$${total.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                </Typography>
                <Typography variant="body2">per year</Typography>
              </Box>
            </Box>
            <Box mt={4} display="flex" justifyContent="center" gap="1em">
              <Button
                variant="contained"
                size="large"
                endIcon={<ChecklistRoundedIcon />}
                component={Link}
                to="/signup?onboarding=true"
              >
                Begin Onboarding
              </Button>
              <Button
                variant="outlined"
                color={theme.palette.mode == 'dark' ? 'secondary' : 'primary'}
                size="large"
                endIcon={
                  showTiers ? (
                    <ExpandLessRoundedIcon />
                  ) : (
                    <ExpandMoreRoundedIcon />
                  )
                }
                onClick={() => setShowTiers((prev: any) => !prev)}
              >
                {showTiers ? 'Hide Pricing Tiers' : 'View Pricing Tiers'}
              </Button>
            </Box>
            <Box textAlign="center" mt={4}>
              <Collapse in={showTiers}>
                <Box mt={2}>
                  <PricingTable
                    licenseTiers={tiers}
                    elevation={0}
                    variant="outlined"
                  />
                </Box>
              </Collapse>
            </Box>
          </Card>
        </>
      )}
      <LoadingCircle isLoading={isLoading} />
    </Box>
  );
};

export default PricingCalculator;
